/**
 * IntegrationsPage — manage external service connections.
 *
 * Displays a provider grid with setup modals, connection status,
 * and test-connection functionality.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plug, CheckCircle2, XCircle, AlertCircle, Loader2,
  Trash2, RefreshCw, Plus, X, Server,
} from 'lucide-react';
import { ConnectBrowserAuthModal } from '../../components/settings/ConnectReadAiModal';

const API_BASE = '/api/integrations';

// ─── Types ──────────────────────────────────────────────────────

interface ProviderField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
}

interface ProviderDef {
  id: string;
  name: string;
  description: string;
  auth_type: string;
  fields: ProviderField[];
  icon: string;
  category: string;
}

interface Integration {
  id: string;
  workspace_id: string;
  provider: string;
  display_name: string;
  auth_type: string;
  config: Record<string, unknown>;
  status: 'connected' | 'disconnected' | 'error';
  last_synced_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Provider Icon ───────────────────────────────────────────────

const PROVIDER_COLORS: Record<string, { bg: string; text: string }> = {
  hubspot:          { bg: '#ff7a59', text: '#fff' },
  figma:            { bg: '#a259ff', text: '#fff' },
  github:           { bg: '#24292e', text: '#fff' },
  notion:           { bg: '#000000', text: '#fff' },
  webflow:          { bg: '#4353ff', text: '#fff' },
  mic:              { bg: '#6366f1', text: '#fff' },
  linkedin:         { bg: '#0a66c2', text: '#fff' },
  'file-signature': { bg: '#2563eb', text: '#fff' },
  mail:             { bg: '#059669', text: '#fff' },
  archie:           { bg: '#f97316', text: '#fff' },
  bot:              { bg: '#d4a27f', text: '#fff' },
  shield:           { bg: '#6d28d9', text: '#fff' },
  database:         { bg: '#dc2626', text: '#fff' },
};

const PROVIDER_LETTERS: Record<string, string> = {
  hubspot: 'H', figma: 'F', github: 'G', notion: 'N',
  webflow: 'W', mic: 'R', linkedin: 'in', 'file-signature': 'D', mail: 'N',
  archie: 'A', bot: 'C', shield: 'W', database: 'S',
};

/** Providers that use Browserbase iframe login instead of credential form */
const BROWSER_AUTH_PROVIDERS = new Set(['readai', 'linkedin', 'claude']);

/** Providers using shared WorkOS SSO — no credentials needed, connect immediately */
const WORKOS_SSO_PROVIDERS = new Set([
  'archie-helsinki', 'archie-alpha', 'archie-demo', 'archie-production', 'archie-preview',
]);

const ProviderIcon: React.FC<{ icon: string; size?: number }> = ({ icon, size = 28 }) => {
  const colors = PROVIDER_COLORS[icon] ?? { bg: '#6b7280', text: '#fff' };
  return (
    <div
      className="rounded-xl flex items-center justify-center font-semibold shrink-0"
      style={{
        width: size + 12, height: size + 12,
        fontSize: size * 0.5,
        backgroundColor: colors.bg,
        color: colors.text,
      }}
    >
      {PROVIDER_LETTERS[icon] || <Server size={size * 0.5} />}
    </div>
  );
};

// ─── Status Indicator ───────────────────────────────────────────

const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'connected') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600">
        <span className="w-2 h-2 rounded-full bg-green-500 ring-2 ring-green-200" />
        Connected
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600">
        <span className="w-2 h-2 rounded-full bg-red-500 ring-2 ring-red-200" />
        Error
      </span>
    );
  }
  return null;
};

// ─── Setup Modal ─────────────────────────────────────────────────

interface SetupModalProps {
  provider: ProviderDef;
  onClose: () => void;
  onSave: (credentials: Record<string, string>, config: Record<string, string>) => Promise<void>;
  saving: boolean;
}

const SetupModal: React.FC<SetupModalProps> = ({ provider, onClose, onSave, saving }) => {
  const [values, setValues] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const credentials: Record<string, string> = {};
    const config: Record<string, string> = {};
    for (const field of provider.fields) {
      if (field.type === 'password' || field.type === 'textarea' || field.key.includes('token') || field.key.includes('key')) {
        credentials[field.key] = values[field.key] || '';
      } else {
        config[field.key] = values[field.key] || '';
      }
    }
    onSave(credentials, config);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Modal header */}
        <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <ProviderIcon icon={provider.icon} size={24} />
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Connect {provider.name}</h3>
                <p className="text-xs text-neutral-500 mt-0.5">{provider.description}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-400 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {provider.fields.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                {field.label} {field.required && <span className="text-red-400">*</span>}
              </label>
              {field.type === 'select' && field.options ? (
                <select
                  value={values[field.key] || ''}
                  onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                  className="w-full px-3.5 py-2.5 border border-neutral-200 rounded-lg text-sm bg-white
                             focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                  required={field.required}
                >
                  <option value="">Select...</option>
                  {field.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea
                  value={values[field.key] || ''}
                  onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                  rows={5}
                  className="w-full px-3.5 py-2.5 border border-neutral-200 rounded-lg text-sm font-mono leading-relaxed bg-neutral-50
                             focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                  required={field.required}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                />
              ) : (
                <input
                  type={field.type === 'password' ? 'password' : 'text'}
                  value={values[field.key] || ''}
                  onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                  placeholder={field.type === 'url' ? 'https://...' : ''}
                  className="w-full px-3.5 py-2.5 border border-neutral-200 rounded-lg text-sm bg-white
                             focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                  required={field.required}
                />
              )}
            </div>
          ))}

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-neutral-600 border border-neutral-200 rounded-lg
                         hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-neutral-900 rounded-lg
                         hover:bg-neutral-800 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />}
              {saving ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────

export const IntegrationsPage: React.FC = () => {
  const [providers, setProviders] = useState<ProviderDef[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [setupProvider, setSetupProvider] = useState<ProviderDef | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [browserAuthProvider, setBrowserAuthProvider] = useState<'readai' | 'linkedin' | null>(null);

  const fetchData = useCallback(async (validate = false) => {
    try {
      const validateParam = validate ? '&validate=true' : '';
      const [provRes, intRes] = await Promise.all([
        fetch(`${API_BASE}/providers`),
        fetch(`${API_BASE}?workspace_id=default${validateParam}`),
      ]);
      if (provRes.ok) {
        const data = await provRes.json();
        setProviders(data.providers || []);
      }
      if (intRes.ok) {
        const data = await intRes.json();
        setIntegrations(data.integrations || []);
      }
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate on first load so stale statuses are caught immediately
  useEffect(() => { fetchData(true); }, [fetchData]);

  /** Connect a WorkOS SSO provider — no credentials needed, uses existing session */
  const handleConnectWorkOsSso = async (providerId: string) => {
    setSaving(true);
    try {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provider: providerId }),
      });
      if (res.ok) {
        await fetchData();
      }
    } catch (err) {
      console.error('Failed to connect WorkOS SSO provider:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async (credentials: Record<string, string>, config: Record<string, string>) => {
    if (!setupProvider) return;
    setSaving(true);
    try {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: setupProvider.id,
          credentials,
          config,
        }),
      });
      if (res.ok) {
        setSetupProvider(null);
        await fetchData();
      }
    } catch (err) {
      console.error('Failed to connect:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (id: string) => {
    try {
      await fetch(`${API_BASE}/${id}`, { method: 'DELETE' });
      await fetchData();
    } catch (err) {
      console.error('Failed to disconnect:', err);
    }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      await fetch(`${API_BASE}/${id}/test`, { method: 'POST' });
      await fetchData();
    } catch (err) {
      console.error('Failed to test:', err);
    } finally {
      setTesting(null);
    }
  };

  // Map integrations by provider for quick lookup
  const integrationsByProvider = new Map<string, Integration>();
  for (const int of integrations) {
    integrationsByProvider.set(int.provider, int);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-neutral-400">
        <Loader2 size={20} className="animate-spin mr-2" />
        Loading integrations...
      </div>
    );
  }

  // Group providers by category
  const categories = [...new Set(providers.map(p => p.category))];
  const connectedCount = integrations.filter(i => i.status === 'connected').length;
  const errorCount = integrations.filter(i => i.status === 'error').length;

  return (
    <div className="space-y-8">
      {/* Summary bar */}
      <div className="flex items-center gap-6">
        <p className="text-sm text-neutral-500 flex-1">
          Connect external services to enable Martha agents to access CRM data, design files, and more.
        </p>
        <div className="flex items-center gap-4 text-xs shrink-0">
          {connectedCount > 0 && (
            <span className="flex items-center gap-1.5 text-green-600 font-medium">
              <CheckCircle2 size={14} /> {connectedCount} connected
            </span>
          )}
          {errorCount > 0 && (
            <span className="flex items-center gap-1.5 text-red-600 font-medium">
              <XCircle size={14} /> {errorCount} error{errorCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Provider Grid — by category */}
      {categories.map(category => (
        <div key={category}>
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
              {category}
            </h3>
            <div className="flex-1 h-px bg-neutral-100" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {providers.filter(p => p.category === category).map(provider => {
              const integration = integrationsByProvider.get(provider.id);
              const isConnected = integration?.status === 'connected';
              const isError = integration?.status === 'error';

              return (
                <div
                  key={provider.id}
                  className={`
                    rounded-xl border transition-all duration-200
                    ${isConnected
                      ? 'border-green-200 bg-green-50/40'
                      : isError
                        ? 'border-red-200 bg-red-50/30'
                        : 'border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm'
                    }
                  `}
                >
                  {/* Card content */}
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      <ProviderIcon icon={provider.icon} />
                      <div className="flex-1 min-w-0">
                        {/* Title + status */}
                        <div className="flex items-center gap-2.5 mb-1">
                          <span className="text-sm font-semibold text-neutral-900">{provider.name}</span>
                          {integration && <StatusDot status={integration.status} />}
                        </div>

                        {/* Description */}
                        <p className="text-[13px] text-neutral-500 leading-relaxed mb-1">
                          {provider.description}
                        </p>

                        {/* Auth type */}
                        <span className="text-[10px] uppercase tracking-wider text-neutral-300 font-medium">
                          {provider.auth_type.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>

                    {/* Error message */}
                    {integration?.error_message && (
                      <div className="mt-3 px-3 py-2 rounded-lg bg-red-50 border border-red-100">
                        <p className="text-xs text-red-600 truncate" title={integration.error_message}>
                          <AlertCircle size={11} className="inline mr-1 -mt-0.5" />
                          {integration.error_message}
                        </p>
                      </div>
                    )}

                    {/* Last synced */}
                    {integration?.last_synced_at && (
                      <p className="mt-2 text-[11px] text-neutral-400">
                        Last tested {new Date(integration.last_synced_at).toLocaleString()}
                      </p>
                    )}
                  </div>

                  {/* Card footer — actions */}
                  <div className="px-5 py-3 border-t border-neutral-100 bg-neutral-50/30 rounded-b-xl flex items-center gap-2">
                    {!integration ? (
                      <button
                        onClick={() => {
                          if (WORKOS_SSO_PROVIDERS.has(provider.id)) {
                            handleConnectWorkOsSso(provider.id);
                          } else if (BROWSER_AUTH_PROVIDERS.has(provider.id)) {
                            setBrowserAuthProvider(provider.id as 'readai' | 'linkedin');
                          } else {
                            setSetupProvider(provider);
                          }
                        }}
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium bg-neutral-900 text-white rounded-lg
                                   hover:bg-neutral-800 disabled:opacity-50 transition-colors"
                      >
                        <Plus size={12} /> Connect
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleTest(integration.id)}
                          disabled={testing === integration.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-neutral-600 border border-neutral-200
                                     rounded-lg hover:bg-white disabled:opacity-50 transition-colors"
                        >
                          {testing === integration.id
                            ? <Loader2 size={12} className="animate-spin" />
                            : <RefreshCw size={12} />
                          }
                          Test
                        </button>
                        <button
                          onClick={() => handleDisconnect(integration.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200
                                     rounded-lg hover:bg-red-50 transition-colors"
                        >
                          <Trash2 size={12} /> Disconnect
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Setup Modal */}
      {setupProvider && (
        <SetupModal
          provider={setupProvider}
          onClose={() => setSetupProvider(null)}
          onSave={handleConnect}
          saving={saving}
        />
      )}

      {/* Browser Auth Modal (Read.ai, LinkedIn) */}
      {browserAuthProvider && (
        <ConnectBrowserAuthModal
          open={true}
          provider={browserAuthProvider}
          onClose={() => setBrowserAuthProvider(null)}
          onConnected={() => fetchData()}
        />
      )}
    </div>
  );
};

export default IntegrationsPage;
